import React, { useState, useRef, useImperativeHandle } from 'react';
import styled from 'styled-components';

/**
 * Local replacements for react-form-builder2's own TextInput / TextArea. The
 * library builds those inputs from a fixed prop list that has no `placeholder`
 * on it, so the only way to support one is to own the render. Single vs multi
 * line differ by one element, hence one component behind both blocks.
 */
export type TextFieldVariant = 'input' | 'textarea';

const StyledWrapper = styled.div`
  width: 100%;
  margin: 8px 0;

  .text-field-input {
    width: 100%;
    border: 1px solid #c4c4c4;
    border-radius: 4px;
    padding: 8.5px 14px;
    font-size: 1rem;
    font-family: inherit;
    outline: none;
    box-sizing: border-box;
    transition: border-color 0.15s ease;
  }

  /* !important: the library's own CSS wins ties on plain rules, so this has
     to win outright rather than on specificity. */
  textarea.text-field-input {
    min-height: 96px !important;
    max-height: 640px !important;
    resize: vertical !important;
    overflow: auto !important;
  }

  /* Border width must not change on focus: a 1px -> 2px swap reflows the
     field and shifts everything beside it. */
  .text-field-input:focus {
    border-color: #0069ff;
  }

  &.has-error .text-field-input {
    border-color: #d32f2f;
  }

  .text-field-error-text {
    color: #d32f2f;
    font-size: 0.75rem;
    margin-top: 3px;
  }
`;

interface TextFieldData {
  field_name?: string;
  required?: boolean;
  value?: string;
  placeholder?: string;
}

interface TextFieldProps {
  data: TextFieldData;
  variant: TextFieldVariant;
  onChange?: (value: Record<string, string>) => void;
  read_only?: boolean;
  /**
   * false on the builder canvas, true in the rendered form — the library sets
   * this, and it is the only reliable signal for "is this a live form?".
   */
  mutable?: boolean;
}

const TextFieldInput = React.forwardRef<HTMLInputElement, TextFieldProps>(
  ({ variant, ...props }, ref) => {
    const { field_name, required, value: initialValue, placeholder } = props.data || {};

    const [value, setValue] = useState<string>(initialValue || '');
    const [touched, setTouched] = useState(false);
    const hiddenInputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => hiddenInputRef.current as HTMLInputElement);

    // Only validate in the live form — `mutable` is false on the builder
    // canvas, where a "required" error is noise.
    const showError = props.mutable !== false && touched && required && !value;

    const emitChange = (next: string) => {
      setValue(next);
      if (props.onChange && field_name) {
        props.onChange({ [field_name]: next });
      }
    };

    const shared = {
      className: 'text-field-input',
      name: field_name,
      value,
      placeholder: placeholder || '',
      disabled: props.read_only,
      onBlur: () => setTouched(true),
    };

    return (
      <StyledWrapper className={showError ? 'has-error' : ''}>
        {variant === 'textarea' ? (
          <textarea {...shared} onChange={(e) => emitChange(e.target.value)} />
        ) : (
          <input {...shared} type="text" onChange={(e) => emitChange(e.target.value)} />
        )}
        {showError && <div className="text-field-error-text">This field is required.</div>}
        <input ref={hiddenInputRef} type="hidden" name={field_name} value={value} />
      </StyledWrapper>
    );
  },
);

TextFieldInput.displayName = 'TextFieldInput';


const withVariant = (variant: TextFieldVariant, displayName: string) => {
  const Wrapped = React.forwardRef<HTMLInputElement, Omit<TextFieldProps, 'variant'>>(
    (props, ref) => <TextFieldInput {...props} variant={variant} ref={ref} />,
  );
  Wrapped.displayName = displayName;
  return Wrapped;
};

export const SingleLineTextInput = withVariant('input', 'SingleLineTextInput');
export const MultiLineTextInput = withVariant('textarea', 'MultiLineTextInput');

export default TextFieldInput;
